import React, { useState } from "react";
import {
  Search,
  MapPin,
  Calendar,
  User,
  Ticket,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
} from "lucide-react";
import { getBookingByCode } from "../services/api/bookings";
import { BookingRecord } from "../types";
import { formatIDR } from "../services/utils";
import Swal from "sweetalert2";

export const CheckBooking: React.FC = () => {
  const [bookingCode, setBookingCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [booking, setBooking] = useState<BookingRecord | null>(null);

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingCode.trim()) return;

    setIsLoading(true);
    try {
      const data = await getBookingByCode(bookingCode.trim());
      if (data) {
        setBooking(data);
      } else {
        setBooking(null);
        Swal.fire({
          icon: "error",
          title: "Tidak Ditemukan",
          text: "Pesanan dengan kode booking tersebut tidak ditemukan.",
          confirmButtonColor: "#4F46E5",
        });
      }
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      Swal.fire({
        icon: "error",
        title: "Terjadi Kesalahan",
        text: "Gagal mengecek pesanan. Silakan coba lagi nanti.",
        confirmButtonColor: "#4F46E5",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Cek Status <span className="text-giz-primary">Pesanan</span>
          </h1>
          <p className="text-lg text-gray-600">
            Masukkan kode booking Anda untuk melihat status dan detail tiket.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6 md:p-8 mb-8">
          <form
            onSubmit={handleCheck}
            className="flex flex-col md:flex-row gap-4"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Contoh: GIZ-XXXXX"
                className="w-full bg-gray-50 border border-gray-300 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-giz-primary focus:border-transparent outline-none uppercase font-mono font-bold"
                value={bookingCode}
                onChange={(e) => setBookingCode(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-giz-primary text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Cek Sekarang"
              )}
            </button>
          </form>
        </div>

        {booking && (
          <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden animate-fadeIn">
            <div className="bg-gray-900 p-6 text-white flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                  Status Pesanan
                </p>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      booking.status === "PAID"
                        ? "bg-green-500"
                        : booking.status === "PENDING"
                        ? "bg-orange-500"
                        : "bg-gray-500"
                    }`}
                  >
                    {booking.status === "PAID"
                      ? "Lunas"
                      : booking.status === "PENDING"
                      ? "Menunggu Pembayaran"
                      : booking.status}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                  Kode Booking
                </p>
                <p className="text-xl font-mono font-bold">{booking.code}</p>
              </div>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                    Detail Pemesan
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Nama Lengkap</p>
                        <p className="font-bold text-gray-900">
                          {booking.customerName}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">
                          Tanggal Kunjungan
                        </p>
                        <p className="font-bold text-gray-900">
                          {new Date(booking.date).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                    Detail Tiket
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Ticket className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Jenis Tiket</p>
                        <p className="font-bold text-gray-900">
                          {booking.ticketType} x {booking.pax}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">
                          Total Pembayaran
                        </p>
                        <p className="font-bold text-giz-primary text-xl">
                          Rp {formatIDR(booking.totalPrice)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {booking.status === "PAID" && (
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col items-center">
                  <p className="text-sm font-bold text-gray-800 mb-4">
                    QR Code Tiket Masuk
                  </p>
                  <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm mb-6">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=GIZPARK-${booking.code}`}
                      alt="Ticket QR"
                      className="w-40 h-40"
                      loading="lazy"
                    />
                  </div>
                  <button
                    className="w-full md:w-auto bg-white border border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition-all"
                    onClick={() =>
                      Swal.fire({
                        icon: "success",
                        title: "Berhasil",
                        text: "Tiket berhasil diunduh (Simulasi).",
                      })
                    }
                  >
                    <Download className="w-5 h-5" /> Simpan/Download Tiket
                  </button>
                </div>
              )}

              {booking.status === "PENDING" && (
                <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 text-center">
                  <p className="text-indigo-800 font-bold mb-2">
                    Menunggu Pembayaran
                  </p>
                  <p className="text-indigo-600 text-sm mb-4">
                    Silakan selesaikan pembayaran Anda agar e-tiket dapat segera
                    diterbitkan.
                  </p>
                  <button className="bg-giz-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-all">
                    Bayar Sekarang
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
